import { api, axiosClient } from "@/shared/libs/axiosClient";

// ── Schedule Rotation ─────────────────────────────────────────

export interface GapWarning {
  start_date: string;  // YYYY-MM-DD
  end_date: string;    // YYYY-MM-DD
  message: string;
}

export interface ScheduleRotationResponse {
  id: number;
  team_id: number;
  starts_at: string;
  duration_days: number;
  members: number[][];
  created_at: string | null;
  updated_at: string | null;
  gap_warning: GapWarning | null;
}

export interface ScheduleRotationCreate {
  starts_at: string;      // ISO datetime (UTC)
  duration_days: number;
  members: number[][];    // each inner array is a duty group
}

export interface ScheduleRotationUpdate {
  starts_at?: string;
  duration_days?: number;
  members?: number[][];
}

export async function fetchScheduleRotation(teamId: number): Promise<ScheduleRotationResponse> {
  return api.get<ScheduleRotationResponse>(`/team/${teamId}/schedule-rotation`);
}

export async function createScheduleRotation(
  teamId: number,
  data: ScheduleRotationCreate,
): Promise<ScheduleRotationResponse> {
  return api.post<ScheduleRotationResponse>(`/team/${teamId}/schedule-rotation`, data);
}

export async function updateScheduleRotation(
  teamId: number,
  data: ScheduleRotationUpdate,
): Promise<ScheduleRotationResponse> {
  return api.patch<ScheduleRotationResponse>(`/team/${teamId}/schedule-rotation`, data);
}

export async function deleteScheduleRotation(teamId: number): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(`/team/${teamId}/schedule-rotation`);
}

// ── Schedule Override ─────────────────────────────────────────

export interface ScheduleOverrideResponse {
  id: number;
  team_id: number;
  starts_at: string;
  ends_at: string;
  original_duty_member_ids: number[];
  new_duty_member_ids: number[];
  note: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ScheduleOverrideCreate {
  starts_at: string;            // ISO datetime (UTC)
  ends_at: string;
  new_duty_member_ids: number[];
  note?: string;
}

export async function fetchScheduleOverrides(
  teamId: number,
  after?: string,
  before?: string,
): Promise<ScheduleOverrideResponse[]> {
  return api.get<ScheduleOverrideResponse[]>(`/team/${teamId}/schedule-override`, {
    params: { after, before },
  });
}

export interface ScheduleOverrideUpdate {
  starts_at?: string;
  ends_at?: string;
  new_duty_member_ids?: number[];
  note?: string;
}

export async function createScheduleOverride(
  teamId: number,
  data: ScheduleOverrideCreate,
): Promise<ScheduleOverrideResponse> {
  return api.post<ScheduleOverrideResponse>(`/team/${teamId}/schedule-override`, data);
}

export async function updateScheduleOverride(
  teamId: number,
  overrideId: number,
  data: ScheduleOverrideUpdate,
): Promise<ScheduleOverrideResponse> {
  return api.patch<ScheduleOverrideResponse>(
    `/team/${teamId}/schedule-override/${overrideId}`,
    data,
  );
}

export async function deleteScheduleOverride(
  teamId: number,
  overrideId: number,
): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(`/team/${teamId}/schedule-override/${overrideId}`);
}

// ── Calendar ──────────────────────────────────────────────────

export async function downloadCalendar(teamId: number): Promise<void> {
  let url: string | null = null;
  let a: HTMLAnchorElement | null = null;
  try {
    const response = await axiosClient.get(`/team/${teamId}/calendar`, {
      responseType: "blob",
    });
    url = URL.createObjectURL(new Blob([response.data], { type: "text/calendar" }));
    a = document.createElement("a");
    a.href = url;
    a.download = `oncall-team-${teamId}.ics`;
    document.body.appendChild(a);
    a.click();
  } finally {
    if (a) document.body.removeChild(a);
    if (url) URL.revokeObjectURL(url);
  }
}
